import React, { FC, useEffect, useState } from 'react';
import classNames from 'classnames';
import Svg from 'svg-inline-react';
import RejectedSvg from '@shared/assets/svgs/rejected.svg';
import { Markdown } from '@src/components/Markdown';
import { SalaryPeriodEnum } from '@src/graphql/generated';
import { USER_VERIFICATION_JOB_START_TIME } from '@src/constants';
import { InfoTooltip } from '../../InfoTooltip';
import { WorkHistoryItem } from '../WorkHistoryItem';
import { WorkHistoryProps } from './props';
import './styles.scss';

export const WorkHistory: FC<WorkHistoryProps> = ({
  workExperience,
  convertedSalary,
  originalSalary,
  canStartIn,
  isRelocation,
  isRemote,
  location,
  skills,
  isFakeLoading,
  isVisible,
  workHistory,
  achievements,
  email,
  phone,
}) => {
  const [isSkillsCollapsed, setIsSkillsCollapsed] = useState(true);

  const getSalaryPeriodLabel = (period: SalaryPeriodEnum): string => {
    switch (period) {
      case SalaryPeriodEnum.HOUR:
        return 'Hourly';
      case SalaryPeriodEnum.WEEK:
        return 'Weekly';
      case SalaryPeriodEnum.MONTH:
        return 'Monthly';
      case SalaryPeriodEnum.YEAR:
        return 'Yearly';
      case SalaryPeriodEnum.TOTAL:
        return 'Totally';
    }
  };

  const getLocationLabel = (location?: string, isRemote?: boolean, isRelocation?: boolean): React.ReactElement => {
    return (
      <>
        {location}
        {isRemote && ', Remote'}
        {isRelocation && ', Relocation'}
      </>
    );
  };

  useEffect(() => {
    if (isVisible && !isFakeLoading) {
      setIsSkillsCollapsed(true);
    }
  }, [isVisible]);

  const isLocationMissing = !location?.length && !isRemote && !isRelocation;
  const hasNoLocationAndStartDate = isLocationMissing && !canStartIn;

  const collapsedSkillsAmount = hasNoLocationAndStartDate ? 4 : 5;

  const skillsData = isSkillsCollapsed ? skills?.slice(0, collapsedSkillsAmount) : skills;

  return (
    <div className="ta-carousel-card-work-history">
      {!!workExperience && (
        <div className="ta-carousel-card-work-history__right-section-row">
          <Markdown markdown={workExperience} wrapperClassName="ta-carousel-card-work-history__right-section-text" />
        </div>
      )}

      <div
        className={classNames(
          'ta-carousel-card-work-history__user-additional-info-wrapper',
          hasNoLocationAndStartDate && 'ta-carousel-card-work-history__user-additional-info-wrapper--flex',
        )}
      >
        <div className="ta-carousel-card-work-history__user-additional-info">
          <div className="ta-carousel-card-work-history__user-additional-info-item">
            <div className="ta-carousel-card-work-history__user-additional-info-item-title">Expected salary</div>

            <p className="ta-carousel-card-work-history__salary-info-item">
              <span className="ta-carousel-card-work-history__salary-info-item-salary">
                {convertedSalary?.currency.symbol}
                {convertedSalary?.value && convertedSalary?.value < 100
                  ? convertedSalary?.value.toFixed(2)
                  : convertedSalary && Math.round(convertedSalary?.value)}
                &nbsp;
                {convertedSalary && getSalaryPeriodLabel(convertedSalary?.period)}
              </span>

              {originalSalary?.currency.symbol !== convertedSalary?.currency.symbol && (
                <InfoTooltip
                  tooltipText={
                    <span>
                      The original expectation is {originalSalary?.currency.symbol}
                      {originalSalary && originalSalary?.value < 100
                        ? originalSalary?.value.toFixed(2)
                        : originalSalary && Math.round(originalSalary?.value)}
                      .<br />
                      Conversion might differ with real-time
                      <br />
                      ECB currency rates.
                    </span>
                  }
                />
              )}
            </p>
          </div>

          {!isLocationMissing && (
            <div className="ta-carousel-card-work-history__user-additional-info-item">
              <div className="ta-carousel-card-work-history__user-additional-info-item-title">Location preference</div>

              <p>{getLocationLabel(location, isRemote, isRelocation)}</p>
            </div>
          )}

          {canStartIn && (
            <div className="ta-carousel-card-work-history__user-additional-info-item">
              <div className="ta-carousel-card-work-history__user-additional-info-item-title">Can start in</div>

              <p>{USER_VERIFICATION_JOB_START_TIME[canStartIn].displayValue}</p>
            </div>
          )}
        </div>

        {!!skillsData.length && (
          <div
            className={classNames(
              'ta-carousel-card-work-history__skills-section',
              hasNoLocationAndStartDate && 'ta-carousel-card-work-history__skills-section--margin',
            )}
          >
            <div className="ta-carousel-card-work-history__skills-title">Skills</div>

            <div className="ta-carousel-card-work-history__skills">
              {skillsData?.map(skill => (
                <div className="ta-carousel-card-work-history__skill fade-in" key={skill.id}>
                  {skill.name}
                </div>
              ))}

              {isSkillsCollapsed && skills.length - collapsedSkillsAmount > 0 && (
                <div
                  className="ta-carousel-card-work-history__skill-expand"
                  onClick={() => setIsSkillsCollapsed(!isSkillsCollapsed)}
                >
                  +{skills.length - collapsedSkillsAmount} more
                </div>
              )}
            </div>
          </div>
        )}
      </div>

      <div className="ta-carousel-card-work-history__divider" />

      {(phone || email) && (
        <>
          <div
            className={classNames(
              'ta-carousel-card-work-history__user-additional-info-wrapper',
              'ta-carousel-card-work-history__user-additional-info-wrapper--flex',
            )}
          >
            {phone && (
              <div className="ta-carousel-card-work-history__user-additional-info-item">
                <div className="ta-carousel-card-work-history__user-additional-info-item-title">Phone number</div>

                <p className="ta-carousel-card-work-history__user-additional-info-item-text">{phone}</p>
              </div>
            )}

            {email && (
              <div className="ta-carousel-card-work-history__user-additional-info-item">
                <div className="ta-carousel-card-work-history__user-additional-info-item-title">E-mail</div>

                <p className="ta-carousel-card-work-history__user-additional-info-item-text">{email}</p>
              </div>
            )}
          </div>

          <div className="ta-carousel-card-work-history__divider" />
        </>
      )}

      <div className="ta-carousel-card-work-history__work-history">
        <h3 className="ta-carousel-card-work-history__work-history-title">Work history</h3>

        {workHistory?.length ? (
          workHistory?.map((workItem, index) => <WorkHistoryItem item={workItem} key={index} />)
        ) : (
          <div className="ta-carousel-card-work-history__work-history-empty">
            <Svg className="ta-carousel-card-work-history__work-history-empty-icon" src={RejectedSvg} />

            <div className="ta-carousel-card-work-history__work-history-empty-text">
              This candidate didn’t provide a CV and we are unable to display their work history.
            </div>
          </div>
        )}
      </div>

      {!!achievements?.length && (
        <div className="ta-carousel-card-work-history__right-section-row">
          <h3 className="ta-carousel-card-work-history__right-section-title">Achievements</h3>

          <Markdown markdown={achievements} wrapperClassName="ta-carousel-card-work-history__right-section-text" />
        </div>
      )}
    </div>
  );
};
